import { StateCreator } from "zustand";
import { produce } from "immer";
import { ReportDetailState, UpdateFnPromise } from "../interface";
import { ReportSection } from "~/types/global";
import { findMatchingSections, findSection } from "../utils";

export interface UpdateActions {
  updateSectionEntries: (
    sectionTitle: string,
    updaterFn: UpdateFnPromise,
    testEntryDeletedFn: (entry: any) => boolean,
    testSectionDeletedFn: (section: any) => boolean,
    calculateDataCountFn?: (section: any) => number,
    includeSubsections?: boolean,
    crossSectionUpdate?: { matchingProp: string; updaterFn: UpdateFnPromise },
    targetIndex?: number
  ) => void;
  updateSubsectionWithMainSection: (
    sectionTitle: string,
    subsectionName: string,
    matchingProp: string,
    updaterFn: UpdateFnPromise,
    testEntryDeletedFn: (entry: any) => boolean,
    testSectionDeletedFn: (section: any) => boolean,
    calculateDataCountFn?: (section: any) => number
  ) => void;
}

interface UpdateSectionParams {
  section: ReportSection;
  updaterFn: UpdateFnPromise;
  testSectionDeletedFn: (section: any) => boolean;
  calculateDataCountFn?: (section: any) => number;
  matchingValues?: any[];
  matchingProp?: string;
}

/**
 * Aplica atualizações em uma seção e recalcula seus metadados
 */
function applyUpdatesToSection({
  section,
  updaterFn,
  testSectionDeletedFn,
  calculateDataCountFn,
  matchingValues,
  matchingProp,
}: UpdateSectionParams) {
  section.data.forEach((entry: any, index: number) => {
    // Se temos valores correspondentes, só atualiza entradas que correspondem
    if (matchingValues && matchingProp) {
      if (matchingValues.includes(entry[matchingProp]?.value)) {
        updaterFn(entry, index);
      }
    } else {
      // Caso contrário, atualiza todas as entradas
      updaterFn(entry, index);
    }
  });

  section.is_deleted = testSectionDeletedFn(section);

  if (calculateDataCountFn) {
    section.data_count = calculateDataCountFn(section);
  }
}

export const createUpdateActions: StateCreator<
  ReportDetailState & { actions: any },
  [],
  [],
  UpdateActions
> = (set, get) => ({
  updateSectionEntries: (
    sectionTitle,
    updaterFn,
    testEntryDeletedFn,
    testSectionDeletedFn,
    calculateDataCountFn,
    includeSubsections = false,
    crossSectionUpdate,
    targetIndex
  ) => {
    set((state) => ({
      ui: { ...state.ui, isActionLoading: true },
    }));

    // Usa requestAnimationFrame para não bloquear a UI
    requestAnimationFrame(() => {
      set((state) =>
        produce(state, (draft) => {
          const { reportType, selectedCombinedFilter, sections } = draft.data;

          const sectionsToUpdate = findMatchingSections(
            sections,
            sectionTitle,
            reportType,
            selectedCombinedFilter,
            includeSubsections
          );

          const matchingValues: any[] = [];

          // Evita passar cada entrada pelo proxy do Immer só para a maioria
          // ser ignorada (seções grandes, ex.: Posts).
          const useTargetIndexFastPath = targetIndex !== undefined && !crossSectionUpdate;

          // Atualiza as seções encontradas
          sectionsToUpdate.forEach((section) => {
            if (useTargetIndexFastPath) {
              const entry = section.data[targetIndex];
              if (entry !== undefined) {
                updaterFn(entry, targetIndex);
              }
            } else {
              section.data.forEach((entry: any, index: number) => {
                updaterFn(entry, index);

                // Coleta valores para atualização cruzada
                if (crossSectionUpdate && entry[crossSectionUpdate.matchingProp]?.value) {
                  matchingValues.push(entry[crossSectionUpdate.matchingProp].value);
                }
              });
            }

            section.is_deleted = testSectionDeletedFn(section);

            if (calculateDataCountFn) {
              section.data_count = calculateDataCountFn(section);
            }
          });

          // Aplica atualização cruzada na seção principal se necessário
          if (crossSectionUpdate && matchingValues.length > 0) {
            const mainSection = findSection(
              sections,
              sectionTitle,
              reportType,
              selectedCombinedFilter
            );

            if (mainSection) {
              applyUpdatesToSection({
                section: mainSection,
                updaterFn: crossSectionUpdate.updaterFn,
                testSectionDeletedFn,
                calculateDataCountFn,
                matchingValues,
                matchingProp: crossSectionUpdate.matchingProp,
              });
            }
          }
        })
      );

      // Recalcula os registros da lista de seções
      const actions = get().actions;
      actions.recalculateSectionList();

      // Remove o loading após a atualização
      set((state) => ({
        ui: { ...state.ui, isActionLoading: false },
      }));
    });
  },

  updateSubsectionWithMainSection: (
    sectionTitle,
    subsectionName,
    matchingProp,
    updaterFn,
    testEntryDeletedFn,
    testSectionDeletedFn,
    calculateDataCountFn
  ) => {
    set((state) => ({
      ui: { ...state.ui, isActionLoading: true },
    }));

    requestAnimationFrame(() => {
      set((state) =>
        produce(state, (draft) => {
          const { reportType, selectedCombinedFilter, sections } = draft.data;

          const subsection = findSection(
            sections,
            sectionTitle,
            reportType,
            selectedCombinedFilter,
            subsectionName
          );

          const mainSection = findSection(
            sections,
            sectionTitle,
            reportType,
            selectedCombinedFilter
          );

          if (!subsection || !mainSection) {
            return;
          }

          // Coleta valores correspondentes da subseção
          const matchingValues = subsection.data
            .map((entry: any) => entry[matchingProp]?.value)
            .filter(Boolean);

          // Atualiza a subseção
          applyUpdatesToSection({
            section: subsection,
            updaterFn,
            testSectionDeletedFn,
            calculateDataCountFn,
          });

          // Atualiza entradas correspondentes na seção principal
          applyUpdatesToSection({
            section: mainSection,
            updaterFn,
            testSectionDeletedFn,
            calculateDataCountFn,
            matchingValues,
            matchingProp,
          });
        })
      );

      const actions = get().actions;
      actions.recalculateSectionList();

      set((state) => ({
        ui: { ...state.ui, isActionLoading: false },
      }));
    });
  },
});
